import { Card, Form, FormInstance, Input, Row, Switch, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Header } from "./components/Header";
import { memo, useCallback, useEffect, useState } from "react";
import TextArea from "antd/es/input/TextArea";
import { Attachments } from "../Account/components/View/Admin/HeaderActions/components/EmailsModal/components/Attachments";
import DynamicFieldsDropdown from "./components/DynamicFields";
import { CriteriaContainer } from "./components/Criteria/CriteriaContainer";
import { RecipientsCounter } from "./components/RecipientsCounter";
import { useInitialValues } from "./hooks/useInitialValues";
import { useCriteriaOptions } from "./hooks/useCriteriaOptions";
import { DATE_FORMAT } from "../../lib/constants";
import { convertDayjsToFormat } from "../../lib/convertDayjsToFormat";
import { usePermissions } from "../../lib/hooks/usePermissions";
import { NavigateFunction, useNavigate } from "react-router-dom";
import "./styles.scss";

const MessageFields = memo(({ setIsLoading }: { setIsLoading: React.Dispatch<React.SetStateAction<boolean>> }) => (
  <Row className="w-100 mb-8">
    <Form.Item
      label="Email Subject"
      name="subject"
      className="w-100"
      rules={[
        { required: true, message: "Email subject is required" },
        { max: 200, message: "Email subject cannot exceed 200 characters" }
      ]}
    >
      <Input />
    </Form.Item>

    <Row className="w-100">
      <Form.Item label="Message" className="mb-4 w-100" rules={[{ required: true, message: "Email body is required" }]}>
        <DynamicFieldsDropdown />
        <Form.Item name="body" noStyle>
          <TextArea rows={10} />
        </Form.Item>
      </Form.Item>
    </Row>

    <Attachments clearError={() => {}} setIsLoading={setIsLoading} />
  </Row>
));

export const EmailBulk = () => {
  const [form]: FormInstance[] = Form.useForm();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [request, setRequest] = useState<any>(null);

  const [count, setCount] = useState<number>(0);

  const { initialValues } = useInitialValues();

  const tooltipStyle = { maxWidth: "344px" };

  const { options, defaultCriteria } = useCriteriaOptions();

  const { hasAccessByController } = usePermissions("MailingsCreateBulk");

  const navigate: NavigateFunction = useNavigate();

  const updateRequest = useCallback(() => {
    const fields = form.getFieldsValue();
    const request = convertDayjsToFormat(fields, DATE_FORMAT);
    setRequest(request);
  }, [form, setRequest]);

  useEffect(() => {
    if (!hasAccessByController("Add")) {
      navigate("/not-found");
    }
  }, [hasAccessByController, navigate]);

  return (
    <Row className="d-block">
      <Form
        requiredMark={false}
        form={form}
        initialValues={initialValues}
        layout="vertical"
        className="w-100"
        disabled={isLoading}
      >
        <Header form={form} request={request} count={count} isLoading={isLoading} />
        <Row className="d-flex bulk__container w-100">
          <Row className="bulk__content">
            <Card className="w-100 h-100 criteria__card mb-4">
              <Row className="mx-2">
                <p className="text w-500">Recipient Criteria</p>
              </Row>
              <CriteriaContainer
                form={form}
                options={options}
                defaultCriteria={defaultCriteria}
                updateRequest={updateRequest}
              />
            </Card>
            <Card className="w-100 h-100 counter__card mb-8">
              <Row className="d-flex justify-space-between mx-2">
                <RecipientsCounter request={request} form={form} count={count} setCount={setCount} />
                <Row>
                  <Row className="align-center">
                    <Form.Item name={["criteria", "onePerRecipient"]} valuePropName="checked" noStyle>
                      <Switch size="default" onChange={updateRequest} />
                    </Form.Item>
                    <span className="ml-2">One email per recipient</span>
                    <Tooltip
                      placement="top"
                      overlayStyle={tooltipStyle}
                      title="When enabled, recipients with multiple matching accounts will receive only one email instead of one per account"
                      overlayClassName="text-sm"
                    >
                      <QuestionCircleOutlined className="ml-2 text-md title logo-cursor" />
                    </Tooltip>
                  </Row>
                </Row>
              </Row>
            </Card>

            <MessageFields setIsLoading={setIsLoading} />
          </Row>
        </Row>
      </Form>
    </Row>
  );
};
